import { Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Plus, Image, Edit, Share, MoreHorizontal, Users, Clock, AlertTriangle, X, ChevronLeft, ChevronRight, Lock, Sparkles, Search, ArrowUpDown, Download } from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'
import { useToast } from '@/hooks/use-toast'
import { useAuth, type OrgTier } from '@/contexts/AuthContext'
import { getDrops, deleteDrop as deleteDropFromStore, type Drop } from '@/lib/dropsStore'
import { DEMO_AUTH_ENABLED } from '@/lib/env'
import { useDeleteDropMutation, useDropsQuery, type UiDrop } from '@/data/drops'

// Tier limits for active drops
const TIER_LIMITS: Record<OrgTier, number> = {
  free: 2,
  pro: 10,
  business: 1000,
}

const DEFAULT_ITEMS_PER_PAGE = 6

// Page size options
const PAGE_SIZE_OPTIONS = [6, 10, 25, 50]

// Sort options
type SortOption = 'newest' | 'oldest' | 'name-asc' | 'name-desc' | 'photos-desc' | 'photos-asc'

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name-asc', label: 'Name A-Z' },
  { value: 'name-desc', label: 'Name Z-A' },
  { value: 'photos-desc', label: 'Most Photos' },
  { value: 'photos-asc', label: 'Least Photos' },
]

export default function DropsPage() {
  return DEMO_AUTH_ENABLED ? <DemoDropsPage /> : <RealDropsPage />
}

function DemoDropsPage() {
  const { toast } = useToast()
  const { organization } = useAuth()
  const tier = organization?.tier || 'free'
  const [searchParams, setSearchParams] = useSearchParams()
  const [drops, setDrops] = useState<Drop[]>([])
  const [dropToDelete, setDropToDelete] = useState<Drop | null>(null)
  const [showLimitModal, setShowLimitModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE)

  // Load drops from shared store
  useEffect(() => {
    const loadDrops = () => {
      const storedDrops = getDrops()
      setDrops(storedDrops)
      setLoading(false)
    }

    loadDrops()

    // Refresh drops every 5 seconds
    const interval = setInterval(loadDrops, 5000)
    return () => clearInterval(interval)
  }, [])

  // Helper to check if a drop is expired based on expiresAt
  const isDropExpired = (drop: Drop) => drop.expiresAt && new Date() > drop.expiresAt

  // Get effective status (accounts for expiration)
  const getEffectiveStatus = (drop: Drop): 'active' | 'closed' | 'expired' => {
    if (isDropExpired(drop)) return 'expired'
    return drop.status
  }

  // Count active drops (excludes expired) and check limits
  const activeDropsCount = useMemo(() =>
    drops.filter(d => getEffectiveStatus(d) === 'active').length,
    [drops]
  )
  const dropLimit = TIER_LIMITS[tier]
  const canCreateDrop = activeDropsCount < dropLimit

  // Count drops by effective status for filter buttons
  const closedOrExpiredCount = useMemo(() =>
    drops.filter(d => getEffectiveStatus(d) !== 'active').length,
    [drops]
  )

  // Get filter and page from URL params with validation
  const rawStatus = searchParams.get('status')
  const filter: 'all' | 'active' | 'closed' =
    rawStatus === 'active' || rawStatus === 'closed' ? rawStatus : 'all'

  const rawPage = parseInt(searchParams.get('page') || '1', 10)
  const page = isNaN(rawPage) || rawPage < 1 ? 1 : rawPage

  // Update filter in URL
  const setFilter = (newFilter: 'all' | 'active' | 'closed') => {
    const params = new URLSearchParams(searchParams)
    if (newFilter === 'all') {
      params.delete('status')
    } else {
      params.set('status', newFilter)
    }
    params.set('page', '1') // Reset to page 1 when filter changes
    setSearchParams(params)
  }

  // Update page in URL
  const setPage = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', newPage.toString())
    setSearchParams(params)
  }

  // Filter and sort drops based on status, search query, and sort option
  const filteredDrops = useMemo(() => {
    // First filter
    const filtered = drops.filter((drop) => {
      // Get effective status (accounts for expiration)
      const effectiveStatus = getEffectiveStatus(drop)

      // Filter by status - 'closed' filter shows both closed and expired
      const matchesStatus = filter === 'all' ||
        (filter === 'active' && effectiveStatus === 'active') ||
        (filter === 'closed' && (effectiveStatus === 'closed' || effectiveStatus === 'expired'))

      // Filter by search query (search name and description)
      const query = searchQuery.toLowerCase().trim()
      const matchesSearch = !query ||
        drop.name.toLowerCase().includes(query) ||
        (drop.description && drop.description.toLowerCase().includes(query))

      return matchesStatus && matchesSearch
    })

    // Then sort
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        case 'name-asc':
          return a.name.localeCompare(b.name)
        case 'name-desc':
          return b.name.localeCompare(a.name)
        case 'photos-desc':
          return b.photoCount - a.photoCount
        case 'photos-asc':
          return a.photoCount - b.photoCount
        default:
          return 0
      }
    })

    return sorted
  }, [drops, filter, searchQuery, sortBy])

  // Check if search returned no results
  const isSearchEmpty = searchQuery.trim() !== '' && filteredDrops.length === 0

  // Paginate filtered drops
  const totalPages = Math.ceil(filteredDrops.length / itemsPerPage)
  const paginatedDrops = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage
    return filteredDrops.slice(startIndex, startIndex + itemsPerPage)
  }, [filteredDrops, page, itemsPerPage])

  // Reset to page 1 if current page is out of bounds
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1)
    }
  }, [page, totalPages])

  const hasDrops = drops.length > 0

  // Export filtered drops as CSV
  const handleExportDrops = () => {
    if (filteredDrops.length === 0) {
      toast({
        title: 'No drops to export',
        description: 'Apply different filters or create some drops first.',
        variant: 'destructive',
      })
      return
    }

    // Build CSV content
    const headers = ['Name', 'Description', 'Status', 'Photo Count', 'Contributor Count', 'View Count', 'Created At', 'Expires At']
    const rows = filteredDrops.map(drop => {
      const effectiveStatus = getEffectiveStatus(drop)
      return [
        `"${drop.name.replace(/"/g, '""')}"`,
        `"${(drop.description || '').replace(/"/g, '""')}"`,
        effectiveStatus,
        drop.photoCount,
        drop.contributorCount,
        drop.viewCount,
        drop.createdAt.toISOString(),
        drop.expiresAt ? drop.expiresAt.toISOString() : '',
      ].join(',')
    })

    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url

    // Include filter info in filename
    const filterSuffix = filter === 'all' ? 'all' : filter
    link.download = `drops-${filterSuffix}-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    toast({
      title: 'Export complete',
      description: `Exported ${filteredDrops.length} drop${filteredDrops.length !== 1 ? 's' : ''} to CSV.`,
    })
  }

  const handleDeleteDrop = (dropId: string) => {
    const deleted = deleteDropFromStore(dropId)
    if (deleted) {
      setDrops(getDrops()) // Refresh drops from store
      toast({
        title: 'Drop deleted',
        description: 'The drop has been permanently deleted.',
      })
    } else {
      toast({
        title: 'Failed to delete',
        description: 'Could not delete the drop. Please try again.',
        variant: 'destructive',
      })
    }
    setDropToDelete(null)
  }

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading drops...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Drops</h1>
          <p className="text-muted-foreground">Manage all your photo drops</p>
        </div>
{canCreateDrop ? (
          <Button asChild>
            <Link to="/drops/create">
              <Plus className="mr-2 h-4 w-4" />
              Create Drop
            </Link>
          </Button>
        ) : (
          <Button onClick={() => setShowLimitModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Drop
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search drops..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              // Reset to page 1 when searching
              const params = new URLSearchParams(searchParams)
              params.set('page', '1')
              setSearchParams(params)
            }}
            className="w-full pl-10 pr-10 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <Button
            variant={filter === 'all' ? 'outline' : 'ghost'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All ({drops.length})
          </Button>
          <Button
            variant={filter === 'active' ? 'outline' : 'ghost'}
            size="sm"
            onClick={() => setFilter('active')}
          >
            Active ({activeDropsCount})
          </Button>
          <Button
            variant={filter === 'closed' ? 'outline' : 'ghost'}
            size="sm"
            onClick={() => setFilter('closed')}
          >
            Closed ({closedOrExpiredCount})
          </Button>
        </div>

        {/* Sort Dropdown */}
        <div className="relative">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="appearance-none pl-8 pr-8 py-2 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
            aria-label="Sort drops"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ArrowUpDown className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>

        {/* Clear Filters Button - show when any filter is active */}
        {(searchQuery || filter !== 'all' || sortBy !== 'newest') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchQuery('')
              setSortBy('newest')
              const params = new URLSearchParams()
              params.set('page', '1')
              setSearchParams(params)
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4 mr-1" />
            Clear Filters
          </Button>
        )}

        {/* Export Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportDrops}
          disabled={filteredDrops.length === 0}
        >
          <Download className="h-4 w-4 mr-1" />
          Export ({filteredDrops.length})
        </Button>
      </div>

      {/* Drops Grid or Empty State */}
      {hasDrops ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {paginatedDrops.map((drop) => (
              <DropCard
                key={drop.id}
                drop={drop}
                onDelete={() => setDropToDelete(drop)}
              />
            ))}
          </div>

          {/* Pagination */}
          {(totalPages > 1 || filteredDrops.length > 6) && (
            <div className="flex items-center justify-between pt-4">
              <div className="flex items-center gap-4">
                <p className="text-sm text-muted-foreground">
                  Showing {((page - 1) * itemsPerPage) + 1} to {Math.min(page * itemsPerPage, filteredDrops.length)} of {filteredDrops.length} drops
                </p>
                <div className="flex items-center gap-2">
                  <label htmlFor="pageSize" className="text-sm text-muted-foreground">Per page:</label>
                  <select
                    id="pageSize"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value))
                      setPage(1) // Reset to page 1 when changing page size
                    }}
                    className="px-2 py-1 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                    aria-label="Items per page"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <Button
                      key={pageNum}
                      variant={pageNum === page ? 'default' : 'outline'}
                      size="sm"
                      className="w-8 h-8 p-0"
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-card text-card-foreground rounded-lg border p-12 text-center">
          <Image className="h-12 w-12 text-muted-foreground mx-auto" />
          <h3 className="mt-4 text-lg font-semibold">No drops yet</h3>
          <p className="mt-2 text-muted-foreground">
            Create your first drop to start collecting photos
          </p>
          <Button className="mt-6" asChild>
            <Link to="/drops/create">
              <Plus className="mr-2 h-4 w-4" />
              Create Drop
            </Link>
          </Button>
        </div>
      )}

      {/* Empty search results */}
      {isSearchEmpty && (
        <div className="bg-card text-card-foreground rounded-lg border p-8 text-center">
          <Search className="h-12 w-12 text-muted-foreground mx-auto" />
          <h3 className="mt-4 text-lg font-semibold">No results found</h3>
          <p className="mt-2 text-muted-foreground">
            No drops match "<span className="font-medium">{searchQuery}</span>"
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => setSearchQuery('')}
          >
            Clear Search
          </Button>
        </div>
      )}

      {/* Empty filtered results (not from search) */}
      {hasDrops && filteredDrops.length === 0 && !isSearchEmpty && (
        <div className="bg-card text-card-foreground rounded-lg border p-8 text-center">
          <p className="text-muted-foreground">
            No {filter} drops found
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {dropToDelete && (
        <DeleteDropModal
          drop={dropToDelete}
          onConfirm={() => handleDeleteDrop(dropToDelete.id)}
          onCancel={() => setDropToDelete(null)}
        />
      )}

      {/* Drop Limit Modal */}
      {showLimitModal && (
        <DropLimitModal
          activeCount={activeDropsCount}
          limit={dropLimit}
          tier={tier}
          onClose={() => setShowLimitModal(false)}
        />
      )}
    </div>
  )
}

function RealDropsPage() {
  const { toast } = useToast()
  const { organization } = useAuth()
  const tier = organization?.tier || 'free'
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [dropToDelete, setDropToDelete] = useState<UiDrop | null>(null)

  const { drops, isLoading: loading, error, refetch } = useDropsQuery()
  const deleteDropMutation = useDeleteDropMutation()

  // URL filter
  const rawStatus = searchParams.get('status')
  const filter: 'all' | 'active' | 'closed' =
    rawStatus === 'active' || rawStatus === 'closed' ? rawStatus : 'all'

  const setFilter = (newFilter: 'all' | 'active' | 'closed') => {
    const params = new URLSearchParams(searchParams)
    if (newFilter === 'all') params.delete('status')
    else params.set('status', newFilter)
    setSearchParams(params)
  }

  const isExpired = (drop: UiDrop) => new Date() > drop.closesAt
  const effectiveStatus = (drop: UiDrop): UiDrop['status'] => (isExpired(drop) ? 'expired' : drop.status)

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    return drops
      .filter((d) => {
        const status = effectiveStatus(d)
        const matchesStatus =
          filter === 'all' ||
          (filter === 'active' && status === 'active') ||
          (filter === 'closed' && (status === 'closed' || status === 'expired'))
        const matchesSearch =
          !q || d.name.toLowerCase().includes(q) || (d.description && d.description.toLowerCase().includes(q))
        return matchesStatus && matchesSearch
      })
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
  }, [drops, filter, searchQuery])

  const activeCount = useMemo(() => filtered.filter((d) => effectiveStatus(d) === 'active').length, [filtered])
  const dropLimit = TIER_LIMITS[tier]
  const canCreateDrop = activeCount < dropLimit

  const handleDelete = async () => {
    if (!dropToDelete) return
    try {
      await deleteDropMutation.mutateAsync(dropToDelete.id)
      await refetch()
      toast({ title: 'Drop deleted', description: 'The drop has been permanently deleted.' })
    } catch (err) {
      toast({
        title: 'Failed to delete',
        description: err instanceof Error ? err.message : 'Could not delete drop',
        variant: 'destructive',
      })
    } finally {
      setDropToDelete(null)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading drops...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-lg font-semibold">Failed to load drops</h2>
          <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
          <div className="mt-4">
            <Button onClick={() => refetch()}>Retry</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Drops</h1>
          <p className="text-muted-foreground">Manage all your photo drops</p>
        </div>
        {canCreateDrop ? (
          <Button asChild>
            <Link to="/drops/create">
              <Plus className="mr-2 h-4 w-4" />
              Create Drop
            </Link>
          </Button>
        ) : (
          <Button variant="outline" disabled>
            <Lock className="mr-2 h-4 w-4" />
            Limit reached
          </Button>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')}>
            All ({drops.length})
          </Button>
          <Button variant={filter === 'active' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('active')}>
            Active ({drops.filter((d) => effectiveStatus(d) === 'active').length})
          </Button>
          <Button variant={filter === 'closed' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('closed')}>
            Closed ({drops.filter((d) => effectiveStatus(d) !== 'active').length})
          </Button>
        </div>
        <div className="relative max-w-sm w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search drops..."
            className="w-full pl-10 pr-3 py-2 rounded-lg border bg-background"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-card rounded-2xl border p-12 text-center card-playful">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mx-auto">
            <Image className="h-8 w-8 text-primary" />
          </div>
          <h3 className="mt-6 text-xl font-bold">No drops yet</h3>
          <p className="mt-2 text-muted-foreground max-w-sm mx-auto">Create your first drop to start collecting photos.</p>
          <Button className="mt-8" size="lg" asChild>
            <Link to="/drops/create">
              <Plus className="mr-2 h-4 w-4" />
              Create Drop
            </Link>
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((drop) => (
            <div key={drop.id} className="bg-card rounded-2xl border p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link to={`/drops/${drop.id}`} className="font-semibold hover:underline">
                    {drop.name}
                  </Link>
                  {drop.description && <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{drop.description}</p>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setDropToDelete(drop)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Image className="h-4 w-4" />
                  <span>{drop.photoCount}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Users className="h-4 w-4" />
                  <span>{drop.contributorCount}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  <span>{effectiveStatus(drop) === 'active' ? 'Active' : effectiveStatus(drop) === 'expired' ? 'Expired' : 'Closed'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {dropToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDropToDelete(null)} />
          <div className="relative bg-card text-card-foreground rounded-xl border shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold">Delete drop?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              This will permanently delete <span className="font-medium">{dropToDelete.name}</span>.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDropToDelete(null)} disabled={deleteDropMutation.isPending}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleteDropMutation.isPending}>
                {deleteDropMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface DropCardProps {
  drop: Drop
  onDelete: () => void
}

function DropCard({ drop, onDelete }: DropCardProps) {
  const [showMenu, setShowMenu] = useState(false)

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  // Check if drop has expired based on expiresAt
  const isExpired = drop.expiresAt && new Date() > drop.expiresAt
  const effectiveStatus = isExpired ? 'expired' : drop.status

  // Status badge styling
  const getStatusBadge = () => {
    if (effectiveStatus === 'active') {
      return { className: 'bg-secondary/15 text-secondary', label: 'Active' }
    } else if (effectiveStatus === 'expired') {
      return { className: 'bg-accent/15 text-accent', label: 'Expired' }
    } else {
      return { className: 'bg-muted text-foreground', label: 'Closed' }
    }
  }

  const statusBadge = getStatusBadge()

  return (
    <div className="bg-card rounded-lg border hover:shadow-md transition-shadow">
      {/* Cover Image or Placeholder */}
      <div className="aspect-video bg-muted flex items-center justify-center relative overflow-hidden rounded-t-lg">
        {drop.coverUrl ? (
          <img
            src={drop.coverUrl}
            alt={drop.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <Image className="h-12 w-12 text-muted-foreground" />
        )}
        {/* Status Badge */}
        <div className="absolute top-2 right-2 flex gap-1">
          {drop.isTrial && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Trial
            </span>
          )}
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge.className}`}
          >
            {statusBadge.label}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <Link
          to={`/drops/${drop.id}`}
          className="font-semibold hover:text-primary transition-colors"
        >
          {drop.name}
        </Link>
        {drop.description && (
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
            {drop.description}
          </p>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Image className="h-4 w-4" />
            <span>{drop.photoCount}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            <span>{drop.contributorCount}</span>
          </div>
          {drop.expiresAt && (
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{formatDate(drop.expiresAt)}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-4">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/drops/${drop.id}/edit`}>
              <Edit className="mr-1 h-3 w-3" />
              Edit
            </Link>
          </Button>
          <Button variant="outline" size="sm">
            <Share className="mr-1 h-3 w-3" />
            Share
          </Button>
          <div className="relative ml-auto">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setShowMenu(!showMenu)}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 mt-1 w-36 rounded-md shadow-lg bg-background border z-50">
                  <div className="py-1">
                    <Link
                      to={`/drops/${drop.id}`}
                      className="block px-4 py-2 text-sm hover:bg-muted"
                      onClick={() => setShowMenu(false)}
                    >
                      View Details
                    </Link>
                    <button
                      className="w-full text-left px-4 py-2 text-sm hover:bg-muted"
                      onClick={() => setShowMenu(false)}
                    >
                      Download All
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-muted"
                      onClick={() => {
                        setShowMenu(false)
                        onDelete()
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface DeleteDropModalProps {
  drop: Drop
  onConfirm: () => void
  onCancel: () => void
}

function DeleteDropModal({ drop, onConfirm, onCancel }: DeleteDropModalProps) {
  const [isDeleting, setIsDeleting] = useState(false)

  const handleConfirm = async () => {
    setIsDeleting(true)
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500))
    onConfirm()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-card text-card-foreground rounded-lg shadow-lg max-w-md w-full mx-4 p-6 z-10">
        <button
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          onClick={onCancel}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 text-destructive mb-4">
          <AlertTriangle className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Delete Drop</h2>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Are you sure you want to delete <span className="font-medium text-foreground">"{drop.name}"</span>?
        </p>

        <p className="text-sm text-muted-foreground mb-6">
          This action cannot be undone. All photos ({drop.photoCount}) and contributor data will be permanently deleted.
        </p>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="flex-1"
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Drop'}
          </Button>
        </div>
      </div>
    </div>
  )
}

interface DropLimitModalProps {
  activeCount: number
  limit: number
  tier: OrgTier
  onClose: () => void
}

function DropLimitModal({ activeCount, limit: _limit, tier, onClose }: DropLimitModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-card text-card-foreground rounded-lg shadow-lg max-w-md w-full mx-4 p-6 z-10">
        <button
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 text-orange-600 mb-4">
          <Lock className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Drop Limit Reached</h2>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          You have <span className="font-medium text-foreground">{activeCount} active drop{activeCount !== 1 ? 's' : ''}</span> which is the maximum for the {tier} tier.
        </p>

        <p className="text-sm text-muted-foreground mb-6">
          {tier === 'free' ? (
            <>Close or delete an existing drop, or upgrade to Pro for up to 10 active drops.</>
          ) : tier === 'pro' ? (
            <>Close or delete an existing drop, or upgrade to Business for up to 1,000 active drops.</>
          ) : (
            <>Close or delete an existing drop to create a new one.</>
          )}
        </p>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onClose}
          >
            Close
          </Button>
          {tier !== 'business' && (
            <Button
              type="button"
              className="flex-1"
              asChild
            >
              <Link to="/pricing">Upgrade Plan</Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
